import { useState } from 'react'

/**
 * Hook for creating shortened URLs using the URL shortener API
 * @returns {Object} Object containing createShortUrl function, loading state, and error state
 */
export function useUrlShortener() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    /**
     * Creates a shortened URL
     * @param {string} longUrl - The URL to shorten
     * @param {string|null} customCode - Optional custom short code
     * @returns {Promise<Object>} Promise resolving to the API response with shortUrl, shortCode, etc.
     */
    const createShortUrl = async (longUrl, customCode = null) => {
        setLoading(true)
        setError(null)

        try {
            const apiUrl = import.meta.env.VITE_SHORTENER_API_URL || 'https://beta.makemerry.app'
            const response = await fetch(`${apiUrl}/shorten`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    url: longUrl,
                    ...(customCode && { customCode }),
                }),
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to create short URL')
            }

            const data = await response.json()
            return data
        } catch (err) {
            setError(err.message)
            throw err
        } finally {
            setLoading(false)
        }
    }

    return { createShortUrl, loading, error }
}
